import { defineStore } from 'pinia'
import { markRaw } from 'vue'

import DefaultModal from '@/components/ui/DefaultModal.vue'

const DEFAULT_MAX_WIDTH = 520
let lastFocusedElement = null

if (typeof document !== 'undefined') {
  document.addEventListener('focusin', (event) => {
    if (event.target !== document.body) {
      lastFocusedElement = event.target
    }
  })
}

export const useModalStore = defineStore('modal', {
  state: () => ({
    isOpen: false,
    component: null,
    props: {},
    maxWidth: DEFAULT_MAX_WIDTH,
    persistent: false,
    resolver: null,
    returnFocusElement: null
  }),
  actions: {
    openModal(options = {}) {
      if (this.resolver) {
        throw new Error('Cannot open a modal while another modal is active')
      }

      const { component, props = {}, maxWidth = DEFAULT_MAX_WIDTH, persistent = false } = options

      this.component = markRaw(component || DefaultModal)
      this.props = component
        ? props
        : {
            title: options.title,
            message: options.message,
            warning: options.warning,
            validateButton: options.validateButton,
            cancelButton: options.cancelButton,
            variant: options.variant,
            ...props
          }
      this.maxWidth = maxWidth
      this.persistent = persistent
      if (typeof document !== 'undefined') {
        const activeElement = document.activeElement
        const returnFocusElement =
          activeElement === document.body ? lastFocusedElement : activeElement
        this.returnFocusElement = returnFocusElement ? markRaw(returnFocusElement) : null
      }
      this.isOpen = true

      return new Promise((resolve) => {
        this.resolver = resolve
      })
    },
    closeModal(result = false) {
      const resolver = this.resolver
      const returnFocusElement = this.returnFocusElement

      this.isOpen = false
      this.component = null
      this.props = {}
      this.maxWidth = DEFAULT_MAX_WIDTH
      this.persistent = false
      this.resolver = null
      this.returnFocusElement = null

      resolver?.(result)
      setTimeout(() => {
        if (!this.isOpen && returnFocusElement?.isConnected) {
          returnFocusElement.focus()
        }
      }, 400)
    },
    dismissModal() {
      if (!this.persistent) {
        this.closeModal(false)
      }
    }
  }
})
